import { createSlice } from '@reduxjs/toolkit';

import type { EscritorioState } from '@/types/escritorio/escritorioCrudTypes';

import {
  atualizarEscritorio,
  buscarEscritorioPorId,
  criarEscritorio,
  excluirEscritorio,
  listarEscritorios,
  listarWorkspaces,
} from './actions';

const initialState: EscritorioState = {
  escritorios: [],
  currentEscritorio: null,
  buscaPorIdAtual: null,
  total: 0,
  page: 1,
  limit: 20,
  isLoading: false,
  error: null,
};

const escritorioCrudSlice = createSlice({
  name: 'escritorioCrud',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentEscritorio: (state) => {
      state.currentEscritorio = null;
    },
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(listarWorkspaces.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(listarWorkspaces.fulfilled, (state, action) => {
        state.isLoading = false;
        state.escritorios = action.payload.data;
        state.total = action.payload.total;
      })
      .addCase(listarWorkspaces.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(listarEscritorios.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(listarEscritorios.fulfilled, (state, action) => {
        state.isLoading = false;
        state.escritorios = action.payload.data;
        state.total = action.payload.total;
      })
      .addCase(listarEscritorios.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Buscas por id em paralelo (troca rápida de escritório): só a mais recente vale. A resposta
      // atrasada de uma anterior não pode trocar o escritório atual nem encerrar o loading.
      .addCase(buscarEscritorioPorId.pending, (state, action) => {
        state.buscaPorIdAtual = action.meta.requestId;
        state.isLoading = true;
        state.error = null;
      })
      .addCase(buscarEscritorioPorId.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.buscaPorIdAtual) return;
        state.buscaPorIdAtual = null;
        state.isLoading = false;
        state.currentEscritorio = action.payload;
      })
      .addCase(buscarEscritorioPorId.rejected, (state, action) => {
        if (action.meta.requestId !== state.buscaPorIdAtual) return;
        state.buscaPorIdAtual = null;
        state.isLoading = false;
        state.error = action.payload as string;
      })

      .addCase(criarEscritorio.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(criarEscritorio.fulfilled, (state, action) => {
        state.isLoading = false;
        state.escritorios.unshift(action.payload);
      })
      .addCase(criarEscritorio.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      .addCase(atualizarEscritorio.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(atualizarEscritorio.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.escritorios.findIndex((e) => e.id === action.payload.id);
        if (index !== -1) {
          state.escritorios[index] = action.payload;
        }
        if (state.currentEscritorio?.id === action.payload.id) {
          state.currentEscritorio = action.payload;
        }
      })
      .addCase(atualizarEscritorio.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      .addCase(excluirEscritorio.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(excluirEscritorio.fulfilled, (state, action) => {
        state.isLoading = false;
        state.escritorios = state.escritorios.filter((e) => e.id !== action.payload);
      })
      .addCase(excluirEscritorio.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearCurrentEscritorio, resetState } = escritorioCrudSlice.actions;

export default escritorioCrudSlice.reducer;
